/**
 * usePolling — Tier 5B Pass 2 shared polling primitive.
 *
 *   const stop = usePolling(callback, intervalMs, options);
 *
 * Behaviour:
 *   - Runs `callback` immediately on mount (unless `immediate: false`).
 *   - Schedules `callback` every `intervalMs` while the hook is
 *     "active" (see gating below).
 *   - Automatically clears the interval on unmount, on option
 *     change, or when gating causes deactivation.
 *   - Pauses polling when the browser tab is hidden
 *     (`document.visibilityState === 'hidden'`) unless
 *     `pauseOnHidden: false`. Resumes — and fires an immediate
 *     catch-up call — when the tab becomes visible again.
 *
 * Options:
 *   - enabled       (default true)  — master on/off switch. When
 *                                      false, no polling happens.
 *   - immediate     (default true)  — run `callback` once on mount.
 *   - pauseOnHidden (default true)  — pause while tab is hidden.
 *   - key           (optional)      — endpoint/identity key passed
 *                                      to `pollingRegistry` for dev-
 *                                      mode duplicate detection.
 *
 * Contract: `callback` is kept in a ref so the interval never needs
 * to be recreated just because the caller's function identity
 * changed. Only interval-defining inputs (`intervalMs`, `enabled`,
 * `pauseOnHidden`, `key`) drive the effect.
 */
import { useEffect, useRef } from 'react';
import { registerPoll } from '../lib/pollingRegistry';

const IS_BROWSER = typeof document !== 'undefined';

export default function usePolling(callback, intervalMs, options = {}) {
  const {
    enabled = true,
    immediate = true,
    pauseOnHidden = true,
    key,
  } = options;

  // Stable ref for the callback — avoids reinstalling the interval
  // when the caller passes an inline function.
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !intervalMs || intervalMs <= 0) return undefined;

    const unregister = registerPoll(key);
    let intervalId = null;

    const safeRun = () => {
      try {
        callbackRef.current?.();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[usePolling] callback threw:', err);
      }
    };

    const start = () => {
      if (intervalId !== null) return;
      intervalId = setInterval(safeRun, intervalMs);
    };

    const stop = () => {
      if (intervalId === null) return;
      clearInterval(intervalId);
      intervalId = null;
    };

    const onVisibility = () => {
      if (!pauseOnHidden) return;
      if (document.hidden) {
        stop();
      } else {
        // Tab just became visible again — fire an immediate catch-up
        // so stale data gets refreshed before the next tick, then
        // resume the interval.
        safeRun();
        start();
      }
    };

    // Immediate run on mount (one call, regardless of visibility).
    if (immediate) safeRun();

    // Only start the ticker if the tab is currently visible (or we
    // aren't gating on visibility at all).
    if (!pauseOnHidden || !IS_BROWSER || !document.hidden) {
      start();
    }

    if (pauseOnHidden && IS_BROWSER) {
      document.addEventListener('visibilitychange', onVisibility);
    }

    return () => {
      stop();
      if (pauseOnHidden && IS_BROWSER) {
        document.removeEventListener('visibilitychange', onVisibility);
      }
      unregister();
    };
  }, [enabled, intervalMs, pauseOnHidden, key, immediate]);
}
